import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import ClientHeader from '~components/ClientInfo/ClientHeader';
import { IconSuccess, SearchAdd, WarningIcon2 } from '~misc/icons';
import FormMapping from '../components/Mapping/FormMapping';
import { Link, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { initialPath } from '~misc/paths';
import { useConfigureFooter } from '~hooks/useHelpers';
import useCliente from '~fetchs/useCliente';
import useFunilObraUnidadeMedida from '~fetchs/Funnel/useFunilObraUnidadeMedida';
import useFunilObraGrupoProduto from '~fetchs/useFunilObraGrupoProduto';
import useOnlineStatus from '~hooks/useOnlineStatus';
import { db } from '~db/db';
import { deviceType, isDesktop, isTablet } from 'react-device-detect';
import { formatDateToCustomString } from '~pages/PriceSearchV2/utils/searchHelpers';
import { randomId } from '~pages/Funnel/utils';
import { formatDateByPattern, getDevice, resetFunnel } from '../helpers/funnelHelpers';
import useFunilStatus from '~fetchs/useFunilStatus';
import originControl from '~services/utils/originControl';
import LoaderFull from '~components/Form/components/LoaderFull';
import useRequisicoesOffline from '~fetchs/useRequisicoesOffline';
import MessageForm from '~components/Form/components/MessageForm';
import useFunilGrupoProdutoMapeamentoInicial from '~fetchs/Funnel/useFunilGrupoProdutoMapeamentoInicial';
import { createItemSchemaArray } from '../hooks/useFunnelNewGroupSchema';
import { getReturnRoute } from "../helpers/returnRoutes";
import { getLocation } from '~services/utils/geoLocation';
import Modal from '~components/Modal';
import { IconAlert } from '~misc/icons';

const hasExistingMapping = (mapeamentos = []) =>
  mapeamentos.some(
    (m) => Number(m?.volumeMapeado) > 0 || Number(m?.volumeNegociado) > 0
  );

const FunnelMapping = () => {
  const isOnline = useOnlineStatus();
  const [searchParams] = useSearchParams();
  const origem = searchParams.get("origem");
  const retorno = searchParams.get("retorno");
  const { origens } = originControl();
  const isFromVisitaEscritorio = origem === "roteirizacao";

  const { save } = useRequisicoesOffline();

  const formikRef = useRef();
  const navigate = useNavigate();
  const { parameter: id_cliente } = useParams();
  const clienteId = parseInt(id_cliente);
  const { cliente } = useCliente(id_cliente);

  const [data, setData] = useState([]);
  const [confirmedData, setConfirmedData] = useState([]);
  const [initialize, setInitialize] = useState(false);
  const [initialData, setInitialData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalEscritorio, setEscritorioModal] = useState(false);

  const { optionFunilObraUnidadeMedida, funilObraUnidadeMedidaObjeto } = useFunilObraUnidadeMedida({});
  const { funilObraGruposProduto, optionFunilObraGruposProduto } = useFunilObraGrupoProduto({});
  const { mapeamentoInicialTrilhas } = useFunilGrupoProdutoMapeamentoInicial({});
  const { funilStatusObjBySlug } = useFunilStatus({});

  const byTrail = mapeamentoInicialTrilhas[cliente?.trilhaId ?? 0] ?? {};

  const schema = createItemSchemaArray(Object.values(byTrail)?.filter(e => e.required)?.map(e => e.funilObraGrupoProdutoId));

  const [showMessageForm, setShowMessageForm] = useState({
    text: "",
    type: "success",
    icon: IconSuccess,
  });

  const location = useLocation();

  const isEditing = useMemo(
    () => hasExistingMapping(data),
    [data]
  );

  useConfigureFooter({
    form: true,
    formId: 'novo',
    submitLabel: isEditing ? 'Salvar' : 'Mapear',
    submitVariation: isEditing ? 'green' : null,
    disabled: false,
  });

  const handleReturn = () => {
    const route = getReturnRoute({ retorno, clienteId });
    console.log(route)

    if (!route) {
      navigate(
        `${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento`,
        { replace: true }
      );
      return;
    }

    navigate(route, { replace: true });
  };

  //ARMAZENAMENTO - FUNÇÕES
  const updateOnSuccess = async (response) => {
    if(response && Array.isArray(response)) {
      const data = response;
      await resetFunnel({ data, table: db.funilObra, clienteId });
      await resetFunnel({ data });
      await db.funilObraNaoSalvo.clear();
    }
  }

  const updateOnError = (error) => {
    setLoading(false);
    const message = error?.message ?? "";
    setShowMessageForm({
      text: `Ocorreu um erro durante o envio do funil - ${message ?? ""}`,
      icon: WarningIcon2,
      type: "error",
    });
  }

  const handleSubmit = async () => {
    setLoading(true);
    const funnel = await getFunnelData();

    const todosSemConsumo = funnel.length > 0 && funnel.every((f) => f.semConsumo === true);
    if (todosSemConsumo) {
      setLoading(false);
      setShowModal(true); 
      return;
    }

    let pos = await getLocation();
    for (const e of Object.keys(pos)) 
      pos[e] = pos[e] ? pos[e].toString() : null;

    setConfirmedData(funnel.filter(f => !f.semConsumo).map(f => ({
      ...f,
      volumeNegociado: f.volumeNegociado && f.volumeNegociado !== "" ? f.volumeNegociado : 0,
      volumeMapeado: f.volumeMapeado && f.volumeMapeado !== "" ? f.volumeMapeado : 0,
      dataNegociacao: `${f.dataNegociacao}-01`,
      dispositivo: getDevice(isTablet, isDesktop, deviceType),
      ...pos,
      createdAt: formatDateToCustomString(new Date()),
      funilStatusId: funilStatusObjBySlug['em-negociacao']?.id ?? 0,
      id: 0,
      ativo: true
    })));
  };

  const handleSubmitFunnel = async () => {
    const obj = {
      id: randomId(15),
      type: "funilObra",
      clienteId,
      toSend: confirmedData,
      createdAt: new Date().toISOString(),
      sended: false,
    };

    try {
      const response = await save(obj);

      if (response?.isOffline) {
        setShowMessageForm({
          text: `Funil atualizado com sucesso (modo offline)`,
          icon: IconSuccess,
          type: "success",
        });
        return;
      }

      await updateOnSuccess(response);
      handleReturn();
    } catch (error) {
      updateOnError(error);
    } finally {
      setLoading(false);
    }
  }

   useEffect(() => {
    if (confirmedData?.length) {
      setLoading(true);

      handleSubmitFunnel();
    }
  }, [confirmedData]);
  //FIM ARMAZENAMENTO - FUNÇÕES

  const getFunnelData = async () => {
    let temp = [];
    await db.funilObraNaoSalvo.where({ clienteId: clienteId }).each(e => temp.push(e));

    return temp;
  }

  const updateData = useCallback(
    ({ id, data }) =>{
      db.funilObraNaoSalvo.update(id, data)
    },
    []
  );

  const handleVisitaEscritorio = () => {
    setEscritorioModal(false);
    const returnUrl = getReturnRoute({ retorno: "roteirizacao", clienteId });
    navigate(`${returnUrl}&escritorio=sucesso`, { replace: true });
  };

  useEffect(() => {
    if(initialize) {
      (async() => {
        if(!origem || origem != origens.mapeamento) {
          await db.funilObraNaoSalvo.clear();
          setInitialData(true);
        } else {
          const temp = await getFunnelData();
  
          if(!temp.length) {
            await db.funilObraNaoSalvo.clear();
            setInitialData(true);
          } else {
            setData(temp);
          }
        }
      })()

    }
  }, [initialize]);

  useEffect(() => {
    if(funilObraGruposProduto?.length && initialData && Object.entries(byTrail)?.length) {
      setInitialData(false);
      let temp = [];

      if(!Object.entries(funilObraUnidadeMedidaObjeto)?.length) return;

      const hoje = new Date();

      let volMap = "";
      let volNeg = "";

      const keys = Object.keys(byTrail).map(e => Number(e));

      funilObraGruposProduto.sort((a, b) => {
        if (a.mapeamentoInicial === 0 && b.mapeamentoInicial !== 0) return 1;
        if (b.mapeamentoInicial === 0 && a.mapeamentoInicial !== 0) return -1;

        return a.mapeamentoInicial - b.mapeamentoInicial;
      }).filter(g => keys.includes(g.id)).forEach(g => {
        let medida = funilObraUnidadeMedidaObjeto[`id-${g.unidadeMedidaPadraoId}`] ?? null;

        if(medida) {
          temp.push({
            funilObraGrupoProdutoId: g.id,
            dataNegociacao: formatDateByPattern(hoje, "yyyy-mm"),
            funilObraUnidadeMedidaId: medida?.id,
            volumeMapeado: volMap,
            volumeNegociado: volNeg,
            semConsumo: false,
          });
        }
      });

      (async() => {
        await db.funilObraNaoSalvo.bulkAdd(
          temp.map(t => ({
            ...t,
            clienteId
          }))
        );

        temp = await getFunnelData();

        setData(temp);
      })()
    }
  }, [funilObraGruposProduto, funilObraUnidadeMedidaObjeto, initialData, cliente?.trilhaId, mapeamentoInicialTrilhas])

  useEffect(() => {
    (async() => {
      const count = await db.funilObra.where("clienteId").equals(clienteId).count();

      if(count != 0) {
        if (location.state?.skipRedirect) {
          setInitialize(true);
          return;
        }
        // Remove /mapeamento do histórico de rotas para que o "voltar" não caia
        // nesta página (que redirecionaria de volta) mas sim na rota anterior (ex.: carteira).
        const rotasKey = "rotas";
        try {
          const rotas = JSON.parse(sessionStorage.getItem(rotasKey)) || [];
          const semMapeamento = rotas.filter(
            (r) => !r.endsWith(`/${clienteId}/mapeamento`)
          );
          sessionStorage.setItem(rotasKey, JSON.stringify(semMapeamento));
        } catch {
          // ignora erros de leitura/escrita no sessionStorage
        }
        const params = new URLSearchParams({from: "direto",});
        if (retorno) params.set("retorno", retorno);

        navigate(`${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento?${params.toString()}`, { replace: true });
      } else {
        setInitialize(true);
      }
    })()
  }, []);

  return (
    <>
      {
        loading ? <LoaderFull /> : null
      }

      <ClientHeader dados={cliente} />

      <div className="container">
        <div className={`flex ${isFromVisitaEscritorio ? 'justify-between' : 'justify-end'} my-4 w-full py-4 border-b`}>
          {
            isFromVisitaEscritorio && (
              <Link
                onClick={() => setEscritorioModal(true)}
                className="bg-pendente text-white opacity-100 flex justify-between items-center gap-4 p-4 md:ml-4 md:mt-0 mt-2 !ml-0 rounded-lg font-bold button-search"
              >
                <span>Código Endereço de Escritório</span>
              </Link>
            )
          }
          <Link
            onClick={async (e) => {
              navigate(`${initialPath}/funil-de-obras/${clienteId}/mapeamento/novo-grupo?origem=${origens.mapeamento}`);
            }}
            className="bg-primaria text-white opacity-100 flex justify-between items-center gap-4 p-4 md:ml-4 md:mt-0 mt-2 rounded-lg font-bold button-search"
          >
            <span>Novo Grupo de Produto</span>
            <SearchAdd className="icon" size="20" />
          </Link>
        </div>

        <Formik
          initialValues={data}
          enableReinitialize
          onSubmit={handleSubmit}
          innerRef={formikRef}
          validateOnBlur={true}
          validationSchema={schema}
        >
          {({ values }) => (
            <Form id="novo">
              <div className="bg-white card pt-12 pb-2 px-6 rounded-xl">
                {values?.length > 0 ? (
                  values.map((item, index) => (
                    <>
                      <FormMapping
                        key={index}
                        index={index}
                        funilObraGruposProduto={optionFunilObraGruposProduto}
                        funilObraUnidadeMedidas={optionFunilObraUnidadeMedida}
                        data={item}
                        updateData={updateData}
                      />
                    </>
                  ))
                ) : (
                  <div className="flex items-center justify-center w-full pb-10">
                    <span className="text-gray-500">Nenhum grupo de produto mapeado</span>
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>

        {showMessageForm.text ? (
          <MessageForm
            text={showMessageForm.text}
            type={showMessageForm.type}
            showMessage={showMessageForm}
            setShowMessage={() =>
              setShowMessageForm({
                text: "",
              })
            }
            icon={showMessageForm.icon}
          />
        ) : null}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          <div >
            <IconAlert />
            <p className='my-4 text-3xl'>É necessário mapear o consumo de pelo menos um Grupo de Produto</p>
          </div>
        }
        hideButtonOne={true}
        hideButtonTwo={true}
      />

      <Modal
        isOpen={modalEscritorio}
        onClose={() => setEscritorioModal(false)}
        title={
          <div >
            <IconAlert />
            <p className='my-4 text-3xl'>Atenção</p>
          </div>
        }
        sub={
          <>
            <p className="text-2xl max-w-[400px]">Essa visita corresponde a um endereço de escritório de cliente?</p>
            <div className="gap-4 w-full flex justify-center mt-6">
              <button  onClick={() => setEscritorioModal(false)} className="px-6 py-3 rounded bg-secundaria text-primaria font-bold  hover:bg-white hover:text-secundaria border-2 border-secundaria border-quartenaria transition duration-300 ease-in-out">Voltar</button>
              <button disabled={loading} onClick={handleVisitaEscritorio} className="px-6 py-3 rounded bg-terciaria text-white font-bold  hover:bg-white hover:text-primaria border-2 border-terciaria border-quartenaria transition duration-300 ease-in-out">Confirmar</button>
            </div>
          </>
        }
        hideButtonOne={true}
        hideButtonTwo={true}
      />
    </>
  );
};

export default FunnelMapping;
